import { AnimalTypeEnum, CategoryTypeEnum, UiCategoryExt } from '../../types';

import { Item, PanelData, PanelMap, ConvertOptions } from './types';

export const ROOT_PATH = '/shop';

const BOLD_LINKS_COUNT = 3;
const MAX_BOLD_DEPTH = 2;

function isBold(parentLevel: number, index: number, type?: CategoryTypeEnum) {
    // display first 3 links in Panel1 (level2) and Panel2 (level3)
    // when the category type is not of Brands
    return (
        type !== CategoryTypeEnum.BRANDS &&
        parentLevel !== 3 &&
        index < BOLD_LINKS_COUNT
    );
}

function isSeperator(
    parentLevel: number,
    index: number,
    type?: CategoryTypeEnum,
) {
    // make the last bold link in Panel2 (level3) to be a seperator
    // when the category type is not of Brands
    return (
        type !== CategoryTypeEnum.BRANDS &&
        parentLevel !== 3 &&
        index === BOLD_LINKS_COUNT - 1
    );
}

// OFFERS / OUTLET specials render as flat links (no drill-down), matching
// desktop's SpecialOffersFlyout. Prevents a special with a nested subtree from
// rendering a navigation button desktop never shows.
function hasFlatSpecials(type?: CategoryTypeEnum): boolean {
    return type === CategoryTypeEnum.OFFERS || type === CategoryTypeEnum.OUTLET;
}

function toShowAllItem(category: UiCategoryExt, showAllText: string): Item {
    return {
        animalType: category.animalType,
        description: category.description,
        extId: category.extId,
        level: category.level,
        path: category.path,
        title: showAllText,
        type: category.type,
        highlighted: false,
        active: false,
        hasChildren: false,
        isSpecial: false,
        isShowAll: true,
        isParent: false,
    };
}

function toItem(category: UiCategoryExt, special: boolean): Item {
    const {
        animalType,
        description,
        extId,
        level,
        path,
        title,
        type,
        children,
        specials,
        highlighted,
        active,
    } = category;

    const hasChildren = !!(children?.length || specials?.length);

    return {
        animalType,
        description,
        extId,
        level,
        path,
        title,
        type,
        highlighted,
        active: level !== 2 && active,
        hasChildren,
        isSpecial: special,
    };
}

function convertTrackingIdCategory(type: string): string {
    switch (type) {
        case CategoryTypeEnum.ANIMAL:
            return `-animal`;
        case CategoryTypeEnum.BRANDS:
            return `-topBrands`;
        case CategoryTypeEnum.OFFERS:
            return `-specialOffers`;
        default:
            return '';
    }
}

function convertTrackingIdTitle(title: string): string {
    return title
        .split(' ')
        .map((substr) => substr.replace(' ', '-'))
        .join('-');
}

export function toAnimalTypeEnum(
    animalType?: string,
): AnimalTypeEnum | undefined {
    return Object.values(AnimalTypeEnum).includes(animalType as AnimalTypeEnum)
        ? (animalType as AnimalTypeEnum)
        : undefined;
}

export function convertTrackingId(
    category: UiCategoryExt,
    parentPanel: PanelData,
    index?: number,
): string | undefined {
    if (category.level === 4) return undefined;

    let trackingId = 'header-category-mobile-menu';

    if (category.level === 3) {
        if (parentPanel.currentType) {
            trackingId += convertTrackingIdCategory(parentPanel.currentType);
        }
        trackingId += '-level4-links';
        if (parentPanel.currentTitle) {
            trackingId += `-${convertTrackingIdTitle(
                parentPanel.currentTitle,
            )}`;
        }
        if (index !== undefined) {
            trackingId += `-${index}`;
        }
        trackingId += `-${convertTrackingIdTitle(category.title)}`;
    }

    if (category.level === 2) {
        if (category.type) {
            trackingId += convertTrackingIdCategory(category.type);
        }
        trackingId += '-level3-links';
        trackingId += `-${convertTrackingIdTitle(category.title)}`;
    }

    return trackingId;
}

function addCategoryPanels(
    category: UiCategoryExt,
    parentPanel: PanelData,
    panelMap: PanelMap,
    options: ConvertOptions,
    index?: number,
    depth = 0,
): void {
    const { allProductsText, showAllText, artificial } = options;
    const artificialBold = depth < MAX_BOLD_DEPTH;
    const {
        animalType,
        description,
        extId,
        level,
        path,
        title,
        type,
        children = [],
        specials = [],
        highlighted,
        hasMore,
    } = category;

    const panelItems: Item[] = [];

    if (allProductsText) {
        const parent: UiCategoryExt = {
            animalType,
            description,
            extId,
            level,
            path,
            title: `${allProductsText} ${title}`,
            type,
            highlighted,
            active: false,
            children: [],
            specials: [],
        };

        panelItems.push({ ...toItem(parent, false), isParent: true });
    }

    const childItems = children?.map((childCategory, childIndex) => {
        return {
            ...toItem(childCategory, false),
            isBold: artificial
                ? artificialBold
                : isBold(level, childIndex, type),
            isSeperator: artificial
                ? false
                : isSeperator(level, childIndex, type),
            isParent: false,
        };
    });
    const specialItems = specials?.map((specialChildCategory, specialIndex) => {
        return {
            ...toItem(specialChildCategory, true),
            isBold: artificial
                ? artificialBold
                : isBold(level, childItems.length + specialIndex, type),
            isSeperator: artificial
                ? false
                : isSeperator(level, childItems.length + specialIndex, type),
            isParent: false,
        };
    });

    let listItems: Item[] = [...childItems, ...specialItems];

    // Read from the source data, before the flattening below, so that turning
    // specials into flat links cannot change whether the show all link appears.
    const allLeaves = listItems.every((item) => !item.hasChildren);

    const flattenSpecials = hasFlatSpecials(type);

    if (flattenSpecials) {
        listItems = listItems.map((item) =>
            item.isSpecial ? { ...item, hasChildren: false } : item,
        );
    }

    if (artificial && showAllText && listItems.length > 0) {
        if (allLeaves && hasMore === true) {
            listItems.push(toShowAllItem(category, showAllText));
        }
    }

    const {
        currentTitle: parentTitle,
        currentPath: parentPath,
        currentAnimalType: parentAnimalType,
    } = parentPanel;

    const panel = {
        show: false,
        items: panelItems.concat(listItems),
        currentTitle: title,
        currentAnimalType: parentAnimalType ?? toAnimalTypeEnum(animalType),
        currentPath: path,
        currentType: type,
        parentTitle,
        parentPath,
        trackingId: convertTrackingId(category, parentPanel, index),
    };
    panelMap.set(path, panel);

    children?.forEach((childCategory, childIndex) =>
        addCategoryPanels(
            childCategory,
            panel,
            panelMap,
            options,
            childIndex,
            depth + 1,
        ),
    );
    if (flattenSpecials) {
        // The specials render as links, so they have no panels to drill into.
        return;
    }

    specials?.forEach((specialChildCategory, specialChildIndex) =>
        addCategoryPanels(
            specialChildCategory,
            panel,
            panelMap,
            options,
            specialChildIndex,
            depth + 1,
        ),
    );
}

export function convertPanelMap(
    categories: UiCategoryExt[],
    allProductsText?: string,
    showAllText?: string,
    artificial = false,
): PanelMap {
    const panelMap = new Map<string, PanelData>();
    const options: ConvertOptions = {
        allProductsText,
        showAllText,
        artificial,
    };

    const rootPanel = {
        currentPath: ROOT_PATH,
        show: true,
        items: categories.map((category, index) => {
            return {
                ...toItem(category, false),
                isBold: artificial ? true : index < BOLD_LINKS_COUNT,
                isParent: false,
            };
        }),
    };

    panelMap.set(ROOT_PATH, rootPanel);

    categories.forEach((category) =>
        addCategoryPanels(category, rootPanel, panelMap, options),
    );

    return panelMap;
}
